import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  TablePagination,
  InputAdornment,
  MenuItem,
  Collapse,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import NoteAddRoundedIcon from "@mui/icons-material/NoteAddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";

const Clinical = () => {
  // 1. Live Dark / Light Theme Listener
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Filter States
  const [dosStart, setDosStart] = useState("");
  const [dosEnd, setDosEnd] = useState("");
  const [staff, setStaff] = useState("TruEvv Staff");
  const [clientSearch, setClientSearch] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Pagination & Select
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedDocs, setSelectedDocs] = useState([]);

  // Exact Design System Palette
  const tealMain = "#1E5E5F"; // Form outline border & Top ADD button
  const tealButton = "#245E65"; // Search & Clear buttons
  const addGreen = "#057A44"; // Add Note button
  const labelTeal = "#276A6C"; // Input label teal color
  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const inputBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const rowHoverBg = isDark ? "rgba(56, 189, 248, 0.04)" : "#F8FAFC";

  // Mock Clinical Charting Data based on exact screenshot
  const [chartList, setChartList] = useState([
    {
      id: 1,
      title: "Collateral Intake Note",
      dos: "07/14/2026",
      accountNo: "1023",
      clientName: "Alexander Wright",
      staffName: "Dr. Usman",
      serviceLine: true,
      locked: true,
      printed: true,
      auditReady: true,
      attachments: 2,
      formId: "2",
      formActive: false,
      createdDate: "07/13/2026",
      modifiedDate: "07/14/2026",
    },
    {
      id: 2,
      title: "Neuropsych Evaluation",
      dos: "07/15/2026",
      accountNo: "1022",
      clientName: "Charlotte Bennett",
      staffName: "Dr. Usman",
      serviceLine: true,
      locked: false,
      printed: false,
      auditReady: false,
      attachments: 1,
      formId: "2",
      formActive: false,
      createdDate: "07/13/2026",
      modifiedDate: "07/15/2026",
    },
    {
      id: 3,
      title: "ARMHS Comprehensive Crisis Plan",
      dos: "07/14/2026",
      accountNo: "1021",
      clientName: "Lucas Miller",
      staffName: "TruEvv Clinical",
      serviceLine: true,
      locked: false,
      printed: false,
      auditReady: false,
      attachments: 3,
      formId: "3",
      formActive: false,
      createdDate: "07/14/2026",
      modifiedDate: "07/17/2026",
    },
    {
      id: 4,
      title: "ARMHS Crisis Mitigation Plan",
      dos: "07/24/2026",
      accountNo: "1020",
      clientName: "Sophia Anderson",
      staffName: "TruEvv Clinical",
      serviceLine: false,
      locked: false,
      printed: false,
      auditReady: false,
      attachments: 3,
      formId: "3",
      formActive: false,
      createdDate: "07/14/2026",
      modifiedDate: "07/24/2026",
    },
    {
      id: 5,
      title: "ARMHS Treatment Review Plan",
      dos: "07/20/2026",
      accountNo: "1019",
      clientName: "Oliver Davies",
      staffName: "TruEvv Clinical",
      serviceLine: false,
      locked: false,
      printed: false,
      auditReady: false,
      attachments: 3,
      formId: "3",
      formActive: false,
      createdDate: "07/14/2026",
      modifiedDate: "07/14/2026",
    },
    {
      id: 6,
      title: "ARMHS Crisis De-escalation",
      dos: "07/01/2026",
      accountNo: "1018",
      clientName: "Emma Taylor",
      staffName: "TruEvv Clinical",
      serviceLine: false,
      locked: false,
      printed: false,
      auditReady: false,
      attachments: 3,
      formId: "3",
      formActive: false,
      createdDate: "07/14/2026",
      modifiedDate: "07/14/2026",
    },
    {
      id: 7,
      title: "ARMHS Progress Review Note",
      dos: "07/14/2026",
      accountNo: "1017",
      clientName: "Benjamin Hayes",
      staffName: "TruEvv Clinical",
      serviceLine: false,
      locked: true,
      printed: false,
      auditReady: false,
      attachments: 7,
      formId: "7",
      formActive: false,
      createdDate: "07/14/2026",
      modifiedDate: "07/14/2026",
    },
    {
      id: 8,
      title: "Clinical Diagnostic Consultation",
      dos: "07/10/2026",
      accountNo: "1016",
      clientName: "Isabella Martinez",
      staffName: "Dr. Usman",
      serviceLine: true,
      locked: true,
      printed: true,
      auditReady: true,
      attachments: 4,
      formId: "4",
      formActive: true,
      createdDate: "07/09/2026",
      modifiedDate: "07/10/2026",
    },
    {
      id: 9,
      title: "Physical Rehab Progress Note",
      dos: "07/08/2026",
      accountNo: "1015",
      clientName: "Liam O'Connor",
      staffName: "TruEvv Clinical",
      serviceLine: true,
      locked: true,
      printed: false,
      auditReady: true,
      attachments: 2,
      formId: "5",
      formActive: true,
      createdDate: "07/07/2026",
      modifiedDate: "07/08/2026",
    },
    {
      id: 10,
      title: "Neurological Follow-up Note",
      dos: "07/05/2026",
      accountNo: "1014",
      clientName: "Amelia Wilson",
      staffName: "Dr. Usman",
      serviceLine: true,
      locked: false,
      printed: false,
      auditReady: false,
      attachments: 1,
      formId: "6",
      formActive: false,
      createdDate: "07/04/2026",
      modifiedDate: "07/05/2026",
    },
    {
      id: 11,
      title: "Sleep Apnea Protocol Summary",
      dos: "07/02/2026",
      accountNo: "1013",
      clientName: "Ethan Campbell",
      staffName: "TruEvv Clinical",
      serviceLine: false,
      locked: false,
      printed: false,
      auditReady: false,
      attachments: 0,
      formId: "2",
      formActive: false,
      createdDate: "07/01/2026",
      modifiedDate: "07/02/2026",
    },
  ]);

  const handleClear = () => {
    setDosStart("");
    setDosEnd("");
    setStaff("TruEvv Staff");
    setClientSearch("");
  };

  const handleDelete = (id) => {
    setChartList((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDocs(chartList.map((item) => item.id));
    } else {
      setSelectedDocs([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedDocs((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const filteredCharts = chartList.filter((doc) => {
    const query = clientSearch.toLowerCase();
    const matchName = doc.clientName.toLowerCase().includes(query);
    const matchAccount = doc.accountNo.includes(query);
    const matchTitle = doc.title.toLowerCase().includes(query);
    return matchName || matchAccount || matchTitle;
  });

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      
      {/* =========================================================
          1. HEADER & TOP ADD NOTE BUTTON
         ========================================================= */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: 800,
              color: primaryTextColor,
              letterSpacing: "-0.3px",
            }}
          >
            Clinical / Charting
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: secondaryTextColor, fontWeight: 500 }}>
            Manage clinical documentation, medical charting protocols, and verified intake files.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<NoteAddRoundedIcon sx={{ fontSize: "1.15rem !important" }} />}
          sx={{
            backgroundColor: addGreen,
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: "0.85rem",
            borderRadius: "8px",
            px: 2.8,
            py: 0.9,
            textTransform: "uppercase",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#046337" },
          }}
        >
          ADD NOTE
        </Button>
      </Box>

      {/* Mini KPI Pulse Strip */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            p: 2,
            borderRadius: "14px",
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 1.8,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              backgroundColor: "#D9EBEF",
              color: tealButton,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: "1.2rem" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
              Total Charts
            </Typography>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: primaryTextColor }}>
              {chartList.length}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            borderRadius: "14px",
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 1.8,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              backgroundColor: isDark ? "rgba(16, 185, 129, 0.12)" : "#ECFDF5",
              color: "#10B981",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: "1.2rem" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
              Locked & Signed
            </Typography>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: primaryTextColor }}>
              {chartList.filter((c) => c.locked).length}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            borderRadius: "14px",
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 1.8,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              backgroundColor: isDark ? "rgba(99, 102, 241, 0.12)" : "#EEF2FF",
              color: "#6366F1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <VerifiedUserOutlinedIcon sx={{ fontSize: "1.2rem" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
              Audit Compliance Ready
            </Typography>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: primaryTextColor }}>
              {chartList.filter((c) => c.auditReady).length}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* =========================================================
          2. EXACT DESIGN SYSTEM FORM FILTER CONTAINER
         ========================================================= */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "14px",
          border: `1.5px solid ${tealMain}`,
          p: { xs: 2.5, md: 3 },
          mb: 4,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.03)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: 2.2,
            mb: 2.5,
          }}
        >
          {/* DOS Start */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: labelTeal, mb: 0.6 }}>
              Date of Service Start
            </Typography>
            <TextField
              fullWidth
              type="date"
              size="small"
              value={dosStart}
              onChange={(e) => setDosStart(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                  fontSize: "0.82rem",
                  color: primaryTextColor,
                  "& fieldset": { borderColor: "#CBD5E1" },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
                "& input": { py: "8.5px", px: "12px" },
              }}
            />
          </Box>

          {/* DOS End */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: labelTeal, mb: 0.6 }}>
              Date of Service End
            </Typography>
            <TextField
              fullWidth
              type="date"
              size="small"
              value={dosEnd}
              onChange={(e) => setDosEnd(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                  fontSize: "0.82rem",
                  color: primaryTextColor,
                  "& fieldset": { borderColor: "#CBD5E1" },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
                "& input": { py: "8.5px", px: "12px" },
              }}
            />
          </Box>

          {/* Staff Select */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: labelTeal, mb: 0.6 }}>
              Staff
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={staff}
              onChange={(e) => setStaff(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                  fontSize: "0.82rem",
                  color: primaryTextColor,
                  "& fieldset": { borderColor: "#CBD5E1" },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
                "& .MuiSelect-select": { py: "8.5px", px: "12px" },
              }}
            >
              <MenuItem value="TruEvv Staff">TruEvv Staff</MenuItem>
              <MenuItem value="Dr. Usman">Dr. Usman (Lead)</MenuItem>
              <MenuItem value="All Providers">All Providers</MenuItem>
            </TextField>
          </Box>

          {/* Search Client */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: labelTeal, mb: 0.6 }}>
              Search Client
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search Patient Name or ID..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: secondaryTextColor, fontSize: "1.1rem" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                  fontSize: "0.82rem",
                  color: primaryTextColor,
                  "& fieldset": { borderColor: "#CBD5E1" },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
                "& input": { py: "8.5px", px: "12px" },
              }}
            />
          </Box>
        </Box>

        {/* Bottom Action Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            pt: 2,
            borderTop: `1px solid ${isDark ? "rgba(255, 255, 255, 0.06)" : "#EAEFF4"}`,
          }}
        >
          <Button
            variant="contained"
            onClick={() => setShowAdvanced(!showAdvanced)}
            startIcon={<FilterListRoundedIcon />}
            sx={{
              backgroundColor: isDark ? "rgba(99, 102, 241, 0.2)" : "#4F46E5",
              color: isDark ? "#A5B4FC" : "#FFFFFF",
              borderRadius: "8px",
              fontSize: "0.78rem",
              fontWeight: 700,
              textTransform: "uppercase",
              px: 2,
              mr: "auto",
              boxShadow: "none",
              "&:hover": { backgroundColor: isDark ? "rgba(99, 102, 241, 0.3)" : "#4338CA" },
            }}
          >
            {showAdvanced ? "Hide Filters" : "Show Filters"}
          </Button>

          <Button
            variant="contained"
            startIcon={<RestartAltRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
            onClick={handleClear}
            sx={{
              backgroundColor: tealButton,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: "8px",
              px: 2.6,
              py: 0.85,
              textTransform: "uppercase",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#1C4E54" },
            }}
          >
            CLEAR
          </Button>

          <Button
            variant="contained"
            startIcon={<SearchIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              backgroundColor: tealButton,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: "8px",
              px: 2.6,
              py: 0.85,
              textTransform: "uppercase",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#1C4E54" },
            }}
          >
            SEARCH
          </Button>
        </Box>

        {/* Collapsed Secondary Filters */}
        <Collapse in={showAdvanced}>
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: `1px dashed ${cardBorder}`,
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Chip label="Filter by Locked Documents" variant="outlined" clickable sx={{ color: secondaryTextColor }} />
            <Chip label="Audit Ready Only" variant="outlined" clickable sx={{ color: secondaryTextColor }} />
            <Chip label="Service Line Cleared" variant="outlined" clickable sx={{ color: secondaryTextColor }} />
            <Chip label="Attachments > 0" variant="outlined" clickable sx={{ color: secondaryTextColor }} />
          </Box>
        </Collapse>
      </Box>

      {/* =========================================================
          3. FULL CLINICAL DOCUMENTATION DATA TABLE (VERTICAL SCROLL)
         ========================================================= */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "14px",
          border: "1px solid #EAEFF4",
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 2px 10px rgba(10, 37, 64, 0.03)",
          overflow: "hidden",
        }}
      >
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            backgroundColor: "transparent",
            maxHeight: "520px",
            overflowY: "auto",
            overflowX: "auto",
            "&::-webkit-scrollbar": { width: "6px", height: "6px" },
            "&::-webkit-scrollbar-track": { backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#F8FAFC" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.15)" : "#CBD5E1",
              borderRadius: "10px",
              "&:hover": { backgroundColor: tealMain },
            },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 1250 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: secondaryTextColor,
                    fontWeight: 700,
                    fontSize: "0.74rem",
                    textTransform: "uppercase",
                    backgroundColor: isDark ? "#162238" : "#F8FAFC",
                    borderBottom: "1px solid #EAEFF4",
                    py: 1.6,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={
                      selectedDocs.length > 0 && selectedDocs.length < chartList.length
                    }
                    checked={chartList.length > 0 && selectedDocs.length === chartList.length}
                    onChange={handleSelectAll}
                    sx={{ color: secondaryTextColor, "&.Mui-checked": { color: tealMain } }}
                  />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Title of Document</TableCell>
                <TableCell>DOS</TableCell>
                <TableCell>Account No</TableCell>
                <TableCell>Client Name</TableCell>
                <TableCell>Staff Name</TableCell>
                <TableCell align="center">Service Line</TableCell>
                <TableCell align="center">Locked</TableCell>
                <TableCell align="center">Printed</TableCell>
                <TableCell align="center">Audit Ready</TableCell>
                <TableCell align="center">Attchments</TableCell>
                <TableCell align="center">Form ID</TableCell>
                <TableCell align="center">Form Active</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Modified Date</TableCell>
                <TableCell align="center">Copy</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredCharts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const isSelected = selectedDocs.includes(row.id);

                  return (
                    <TableRow
                      key={row.id}
                      selected={isSelected}
                      sx={{
                        transition: "all 0.15s ease",
                        "&:hover": { backgroundColor: rowHoverBg },
                        "& td": {
                          borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"}`,
                          py: 1.4,
                          fontSize: "0.82rem",
                        },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => handleSelectOne(row.id)}
                          sx={{ color: secondaryTextColor, "&.Mui-checked": { color: tealMain } }}
                        />
                      </TableCell>

                      <TableCell sx={{ fontWeight: 700, color: secondaryTextColor }}>
                        {row.id}
                      </TableCell>

                      <TableCell sx={{ fontWeight: 700, color: primaryTextColor }}>
                        {row.title}
                      </TableCell>

                      <TableCell sx={{ color: secondaryTextColor, fontWeight: 500 }}>
                        {row.dos}
                      </TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            fontFamily: "monospace",
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            color: tealButton,
                            px: 1,
                            py: 0.3,
                            borderRadius: "5px",
                            backgroundColor: "#D9EBEF",
                          }}
                        >
                          #{row.accountNo}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: "0.74rem",
                              fontWeight: 800,
                              backgroundColor: "#D9EBEF",
                              color: tealButton,
                            }}
                          >
                            {row.clientName[0]}
                          </Avatar>
                          <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: primaryTextColor }}>
                            {row.clientName}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ color: secondaryTextColor, fontWeight: 600 }}>
                        {row.staffName}
                      </TableCell>

                      <TableCell align="center">
                        {row.serviceLine ? (
                          <CheckCircleRoundedIcon sx={{ fontSize: "1.1rem", color: "#10B981" }} />
                        ) : (
                          <CancelRoundedIcon sx={{ fontSize: "1.1rem", color: "#EF4444" }} />
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {row.locked ? (
                          <CheckCircleRoundedIcon sx={{ fontSize: "1.1rem", color: "#10B981" }} />
                        ) : (
                          <CancelRoundedIcon sx={{ fontSize: "1.1rem", color: "#EF4444" }} />
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {row.printed ? (
                          <CheckCircleRoundedIcon sx={{ fontSize: "1.1rem", color: "#10B981" }} />
                        ) : (
                          <CancelRoundedIcon sx={{ fontSize: "1.1rem", color: "#EF4444" }} />
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {row.auditReady ? (
                          <CheckCircleRoundedIcon sx={{ fontSize: "1.1rem", color: "#10B981" }} />
                        ) : (
                          <CancelRoundedIcon sx={{ fontSize: "1.1rem", color: "#EF4444" }} />
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, color: secondaryTextColor }}>
                          <AttachFileRoundedIcon sx={{ fontSize: "0.95rem" }} />
                          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700 }}>
                            {row.attachments}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell align="center" sx={{ color: secondaryTextColor, fontWeight: 600 }}>
                        {row.formId}
                      </TableCell>

                      <TableCell align="center">
                        {row.formActive ? (
                          <CheckCircleRoundedIcon sx={{ fontSize: "1.1rem", color: "#10B981" }} />
                        ) : (
                          <CancelRoundedIcon sx={{ fontSize: "1.1rem", color: "#EF4444" }} />
                        )}
                      </TableCell>

                      <TableCell sx={{ color: secondaryTextColor }}>{row.createdDate}</TableCell>

                      <TableCell sx={{ color: secondaryTextColor }}>{row.modifiedDate}</TableCell>

                      <TableCell align="center">
                        <Tooltip title="Duplicate Chart">
                          <IconButton
                            size="small"
                            sx={{ color: secondaryTextColor, "&:hover": { color: tealMain } }}
                          >
                            <ContentCopyRoundedIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title="Delete Document">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.id)}
                            sx={{
                              color: "#EF4444",
                              "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.1)" },
                            }}
                          >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Bar */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 15, 25, 50]}
          component="div"
          count={filteredCharts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: "1px solid #EAEFF4",
            color: secondaryTextColor,
            "& .MuiTablePagination-select": { color: primaryTextColor, fontWeight: 600 },
          }}
        />
      </Box>

    </Box>
  );
};

export default Clinical;